import { useToast } from '@/hooks';
import { getToastMessageApi } from '@/shared/utils/toast';
import {
  ClockCircleOutlined,
  DesktopOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  KeyOutlined,
  MobileOutlined,
  TabletOutlined,
} from "@/components/icon/tabler";
import { useIntl } from "@umijs/max";
import { App, Badge, Button, DatePicker, Empty, Pagination, Segmented, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { createStyles } from "antd-style";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useMemo, useState } from "react";
import { TouchSafeTooltip as Tooltip } from '@/shared/components/TouchSafeTooltip';
import { ConfirmPopover as Popconfirm } from '@/shared/components';

dayjs.extend(relativeTime);

import {
  getLoginHistory,
  getSessions,
  revokeOtherSessions,
  revokeSession,
} from "@/services/auth/api";
import useStyles from "./index.style";

const { Text } = Typography;
const { RangePicker } = DatePicker;

type SessionGroup = {
  key: string;
  representative: AuthAPI.SessionInfo;
  ids: string[];
  count: number;
  hasCurrent: boolean;
  currentId?: string;
  earliestCreatedAt: string;
  latestActiveAt: string;
};

const LOCATION_NOISE = new Set(["Local Network", "Local"]);

const formatDeviceTitle = (info: AuthAPI.SessionInfo["deviceInfo"]) => {
  if (info.deviceName) return info.deviceName;
  const browserSeg = info.browserVersion
    ? `${info.browser} ${info.browserVersion.split(".")[0]}`
    : info.browser;
  const osSeg = info.osVersion ? `${info.os} ${info.osVersion}` : info.os;
  return `${osSeg} · ${browserSeg}`;
};

const formatLocation = (
  location: AuthAPI.SessionInfo["location"],
  ip: string
) => {
  const parts = [location?.city, location?.region, location?.country].filter(
    (s): s is string => !!s && !LOCATION_NOISE.has(s)
  );
  return parts.length > 0 ? `${parts.join(" · ")} · ${ip}` : ip;
};

const useSessionStyles = createStyles(({ token, css }) => ({
  viewWrap: css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginLG}px;
  `,
  switchBar: css`
    display: flex;
  `,

  list: css`
    display: flex;
    flex-direction: column;
  `,
  card: css`
    display: flex;
    align-items: flex-start;
    gap: ${token.padding}px;
    padding: ${token.padding}px 0;
    transition: background 0.15s;
    border-radius: ${token.borderRadius}px;
    & + & {
      border-top: 1px solid ${token.colorSplit};
    }
    &:hover {
      background: ${token.colorFillQuaternary};
    }
  `,
  cardCurrent: css`
    background: ${token.controlItemBgActive};
    & + & {
      border-top: none;
    }
    &:hover {
      background: ${token.controlItemBgActiveHover};
    }
  `,
  iconWrap: css`
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${token.borderRadius}px;
    background: ${token.colorFillTertiary};
    font-size: 18px;
    color: ${token.colorTextSecondary};
  `,
  main: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
    flex-wrap: wrap;
  `,
  title: css`
    font-size: 14px;
    font-weight: 500;
    color: ${token.colorText};
  `,
  metaRow: css`
    display: flex;
    align-items: center;
    gap: ${token.padding}px;
    font-size: 12px;
    color: ${token.colorTextTertiary};
    flex-wrap: wrap;
    .anticon {
      margin-right: 4px;
      font-size: 12px;
    }
  `,
  metaItem: css`
    display: inline-flex;
    align-items: center;
  `,
  action: css`
    flex: 0 0 auto;
    align-self: center;
  `,
  paginationWrap: css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${token.marginSM}px;
  `,
}));

const SessionsView: React.FC = () => {
  const intl = useIntl();
  const { modal } = App.useApp();
  const message = useToast().raw;
  const { styles } = useStyles();
  const { styles: sStyles, cx } = useSessionStyles();

  const [activeView, setActiveView] = useState<"sessions" | "history">(
    "sessions"
  );

  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessions, setSessions] = useState<AuthAPI.SessionInfo[]>([]);
  const [sessionsPagination, setSessionsPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRecords, setHistoryRecords] = useState<
    AuthAPI.LoginHistoryRecord[]
  >([]);
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [historyFilters, setHistoryFilters] = useState<{
    status?: "success" | "failed";
    dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  }>({});

  const loadSessions = async (page = 1, pageSize = 50) => {
    setSessionsLoading(true);
    try {
      const response = await getSessions({ page, pageSize, active: true });
      if (response.success && response.data) {
        setSessions(response.data.sessions || []);
        setSessionsPagination({
          current: response.data.page,
          pageSize: response.data.pageSize,
          total: response.data.total,
        });
      } else {
        getToastMessageApi().error(
          intl.formatMessage({
            id: "pages.account.settings.sessions.loadFailed",
            defaultMessage: "加载失败",
          })
        );
        console.warn("[sessions] load failed", response);
      }
    } catch (error: any) {
      getToastMessageApi().error(
        intl.formatMessage({
          id: "pages.account.settings.sessions.loadFailed",
          defaultMessage: "加载失败",
        })
      );
      console.warn("[sessions] load error", error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadHistory = async (page = 1, pageSize = 10) => {
    setHistoryLoading(true);
    try {
      const params: AuthAPI.GetLoginHistoryParams = { page, pageSize };
      if (historyFilters.status) {
        params.status = historyFilters.status;
      }
      if (historyFilters.dateRange) {
        params.startDate = historyFilters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = historyFilters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await getLoginHistory(params);
      if (response.success && response.data) {
        setHistoryRecords(response.data.records || []);
        setHistoryPagination({
          current: response.data.page,
          pageSize: response.data.pageSize,
          total: response.data.total,
        });
      } else {
        getToastMessageApi().error(
          intl.formatMessage({
            id: "pages.account.settings.sessions.loadFailed",
            defaultMessage: "加载失败",
          })
        );
        console.warn("[sessions] history load failed", response);
      }
    } catch (error: any) {
      getToastMessageApi().error(
        intl.formatMessage({
          id: "pages.account.settings.sessions.loadFailed",
          defaultMessage: "加载失败",
        })
      );
      console.warn("[sessions] history load error", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleRevokeOthers = () => {
    modal.confirm({
      title: intl.formatMessage({
        id: "pages.account.settings.sessions.revokeOthersConfirmTitle",
        defaultMessage: "下线所有其他设备",
      }),
      content: intl.formatMessage({
        id: "pages.account.settings.sessions.revokeOthersConfirmContent",
        defaultMessage:
          "确定要下线除当前设备外的所有其他设备吗？此操作不可撤销。",
      }),
      okText: intl.formatMessage({
        id: "pages.account.settings.sessions.confirm",
        defaultMessage: "确定",
      }),
      cancelText: intl.formatMessage({
        id: "pages.account.settings.sessions.cancel",
        defaultMessage: "取消",
      }),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await revokeOtherSessions();
          if (response.success) {
            message.success(
              intl.formatMessage(
                {
                  id: "pages.account.settings.sessions.revokeOthersSuccess",
                  defaultMessage: "已下线 {count} 个设备",
                },
                { count: response.data?.revokedCount || 0 }
              )
            );
            loadSessions(1, sessionsPagination.pageSize);
          } else {
            getToastMessageApi().error(
              intl.formatMessage({
                id: "pages.account.settings.sessions.revokeOthersFailed",
                defaultMessage: "下线失败",
              })
            );
            console.warn("[sessions] revoke others failed", response);
          }
        } catch (error: any) {
          getToastMessageApi().error(
            intl.formatMessage({
              id: "pages.account.settings.sessions.revokeOthersFailed",
              defaultMessage: "下线失败",
            })
          );
          console.warn("[sessions] revoke others error", error);
        }
      },
    });
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case "desktop":
        return <DesktopOutlined />;
      case "mobile":
        return <MobileOutlined />;
      case "tablet":
        return <TabletOutlined />;
      default:
        return <DesktopOutlined />;
    }
  };

  const getLoginMethodText = (method: string) => {
    switch (method) {
      case "password":
        return intl.formatMessage({
          id: "pages.account.settings.sessions.loginMethod.password",
          defaultMessage: "密码登录",
        });
      case "google":
        return intl.formatMessage({
          id: "pages.account.settings.sessions.loginMethod.google",
          defaultMessage: "Google 登录",
        });
      case "apikey":
        return intl.formatMessage({
          id: "pages.account.settings.sessions.loginMethod.apikey",
          defaultMessage: "API Key",
        });
      default:
        return method;
    }
  };

  const groupedSessions = useMemo<SessionGroup[]>(() => {
    const map = new Map<string, AuthAPI.SessionInfo[]>();
    for (const s of sessions) {
      const fp = [
        s.deviceInfo.deviceType,
        s.deviceInfo.os,
        s.deviceInfo.osVersion ?? "",
        s.deviceInfo.browser,
        s.deviceInfo.browserVersion ?? "",
        s.ipAddress,
        s.loginMethod,
      ].join("|");
      if (!map.has(fp)) map.set(fp, []);
      map.get(fp)?.push(s);
    }
    const groups: SessionGroup[] = [];
    for (const [key, list] of map) {
      const sorted = [...list].sort(
        (a, b) =>
          dayjs(b.lastActiveAt).valueOf() - dayjs(a.lastActiveAt).valueOf()
      );
      const current = sorted.find((s) => s.isCurrent);
      const representative = current ?? sorted[0];
      const earliestCreatedAt = sorted.reduce(
        (min, s) =>
          dayjs(s.createdAt).valueOf() < dayjs(min).valueOf()
            ? s.createdAt
            : min,
        sorted[0].createdAt
      );
      groups.push({
        key,
        representative,
        ids: sorted.map((s) => s.id),
        count: sorted.length,
        hasCurrent: !!current,
        currentId: current?.id,
        earliestCreatedAt,
        latestActiveAt: sorted[0].lastActiveAt,
      });
    }
    return groups.sort((a, b) => {
      if (a.hasCurrent && !b.hasCurrent) return -1;
      if (!a.hasCurrent && b.hasCurrent) return 1;
      return (
        dayjs(b.latestActiveAt).valueOf() - dayjs(a.latestActiveAt).valueOf()
      );
    });
  }, [sessions]);

  const handleRevokeGroup = async (group: SessionGroup) => {
    const ids = group.ids.filter((id) => id !== group.currentId);
    if (ids.length === 0) return;
    let okCount = 0;
    for (const id of ids) {
      try {
        const r = await revokeSession(id);
        if (r.success) okCount++;
        else console.warn("[sessions] revoke failed", r);
      } catch (e: any) {
        console.warn("[sessions] revoke error", e);
      }
    }
    if (okCount > 0) {
      message.success(
        intl.formatMessage(
          {
            id: "pages.account.settings.sessions.revokeGroupSuccess",
            defaultMessage: "已下线 {count} 个会话",
          },
          { count: okCount }
        )
      );
      loadSessions(sessionsPagination.current, sessionsPagination.pageSize);
    } else {
      getToastMessageApi().error(
        intl.formatMessage({
          id: "pages.account.settings.sessions.revokeFailed",
          defaultMessage: "下线失败",
        })
      );
    }
  };

  const renderGroupCard = (group: SessionGroup) => {
    const item = group.representative;
    const title = formatDeviceTitle(item.deviceInfo);
    const locText = formatLocation(item.location, item.ipAddress);
    const revocableCount = group.ids.length - (group.hasCurrent ? 1 : 0);
    return (
      <div
        key={group.key}
        className={cx(sStyles.card, group.hasCurrent && sStyles.cardCurrent)}
      >
        <div className={sStyles.iconWrap}>
          {getDeviceIcon(item.deviceInfo.deviceType)}
        </div>
        <div className={sStyles.main}>
          <div className={sStyles.titleRow}>
            <span className={sStyles.title}>{title}</span>
            {group.hasCurrent && (
              <Tag style={{ marginInlineEnd: 0 }}>
                {intl.formatMessage({
                  id: "pages.account.settings.sessions.currentDevice",
                  defaultMessage: "当前设备",
                })}
              </Tag>
            )}
            <Tag icon={<KeyOutlined />} style={{ marginInlineEnd: 0 }}>
              {getLoginMethodText(item.loginMethod)}
            </Tag>
            {group.count > 1 && (
              <Tag style={{ marginInlineEnd: 0 }}>
                {intl.formatMessage(
                  {
                    id: "pages.account.settings.sessions.groupCount",
                    defaultMessage: "{count} 个会话",
                  },
                  { count: group.count }
                )}
              </Tag>
            )}
          </div>
          <div className={sStyles.metaRow}>
            <span className={sStyles.metaItem}>
              <EnvironmentOutlined />
              {locText}
            </span>
            <span className={sStyles.metaItem}>
              <ClockCircleOutlined />
              <Tooltip
                title={dayjs(group.latestActiveAt).format("YYYY-MM-DD HH:mm")}
              >
                {intl.formatMessage(
                  {
                    id: "pages.account.settings.sessions.lastActive",
                    defaultMessage: "最后活跃 {time}",
                  },
                  { time: dayjs(group.latestActiveAt).fromNow() }
                )}
              </Tooltip>
            </span>
            <span className={sStyles.metaItem}>
              {group.count > 1
                ? intl.formatMessage(
                    {
                      id: "pages.account.settings.sessions.earliestLogin",
                      defaultMessage: "最早登录 {time}",
                    },
                    { time: dayjs(group.earliestCreatedAt).fromNow() }
                  )
                : intl.formatMessage(
                    {
                      id: "pages.account.settings.sessions.loggedInAt",
                      defaultMessage: "登录于 {time}",
                    },
                    { time: dayjs(item.createdAt).fromNow() }
                  )}
            </span>
          </div>
        </div>
        <div className={sStyles.action}>
          {revocableCount > 0 && (
            <Popconfirm
              title={intl.formatMessage(
                {
                  id: "pages.account.settings.sessions.revokeGroupConfirm",
                  defaultMessage: "确定要下线此设备的 {count} 个会话吗？",
                },
                { count: revocableCount }
              )}
              onConfirm={() => handleRevokeGroup(group)}
              okText={intl.formatMessage({
                id: "pages.account.settings.sessions.confirm",
                defaultMessage: "确定",
              })}
              cancelText={intl.formatMessage({
                id: "pages.account.settings.sessions.cancel",
                defaultMessage: "取消",
              })}
              okButtonProps={{ danger: true }}
            >
              <Button danger>
                {intl.formatMessage({
                  id: "pages.account.settings.sessions.revoke",
                  defaultMessage: "下线",
                })}
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>
    );
  };

  const historyColumns: ColumnsType<AuthAPI.LoginHistoryRecord> = [
    {
      title: intl.formatMessage({
        id: "pages.account.settings.sessions.column.time",
        defaultMessage: "时间",
      }),
      key: "when",
      width: 200,
      render: (_, record) => (
        <Space size={6}>
          <Badge status={record.status === "success" ? "success" : "error"} />
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {dayjs(record.createdAt).format("YYYY-MM-DD HH:mm:ss")}
          </span>
        </Space>
      ),
    },
    {
      title: intl.formatMessage({
        id: "pages.account.settings.sessions.column.device",
        defaultMessage: "设备",
      }),
      key: "device",
      ellipsis: true,
      render: (_, record) => {
        const deviceTitle = formatDeviceTitle(record.deviceInfo);
        const locText = formatLocation(record.location, record.ipAddress);
        return (
          <Space size={8} align="start">
            <span style={{ fontSize: 16, color: "inherit" }}>
              {getDeviceIcon(record.deviceInfo.deviceType)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div>{deviceTitle}</div>
              <Text
                type="secondary"
                style={{ fontSize: 12 }}
                ellipsis={{ tooltip: locText }}
              >
                {locText}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "pages.account.settings.sessions.column.loginMethod",
        defaultMessage: "登录方式",
      }),
      dataIndex: "loginMethod",
      key: "loginMethod",
      width: 110,
      render: (method: string) => (
        <Tag style={{ marginInlineEnd: 0 }}>{getLoginMethodText(method)}</Tag>
      ),
    },
  ];

  const renderSessionsTab = () => (
    <div className={styles.viewRoot}>
      <div className={styles.viewHeader}>
        <div className={styles.viewHeaderExtra}>
          <Button
            danger
            onClick={handleRevokeOthers}
            disabled={sessions.length <= 1}
          >
            {intl.formatMessage({
              id: "pages.account.settings.sessions.revokeOthers",
              defaultMessage: "下线所有其他设备",
            })}
          </Button>
        </div>
      </div>

      {sessionsLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : sessions.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={intl.formatMessage({
            id: "pages.account.settings.sessions.empty",
            defaultMessage: "暂无会话",
          })}
        />
      ) : (
        <>
          <div className={sStyles.list}>
            {groupedSessions.map((g) => renderGroupCard(g))}
          </div>
          {sessionsPagination.total > sessionsPagination.pageSize && (
            <div className={sStyles.paginationWrap}>
              {}
              <Pagination
                {...sessionsPagination}
                showSizeChanger={false}
                showTotal={(total) =>
                  intl.formatMessage(
                    {
                      id: "pages.account.settings.sessions.total",
                      defaultMessage: "共 {total} 个会话",
                    },
                    { total }
                  )
                }
                onChange={(page) =>
                  loadSessions(page, sessionsPagination.pageSize)
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderHistoryTab = () => (
    <div className={styles.viewRoot}>
      <div className={styles.viewHeader}>
        <div className={styles.viewHeaderDesc}>
          {intl.formatMessage({
            id: "pages.account.settings.sessions.historyDeprojection",
            defaultMessage: "查看您的登录历史记录，记录保留 90 天。",
          })}
        </div>
        <div className={styles.viewHeaderExtra}>
          <Space wrap>
            <Select
              placeholder={intl.formatMessage({
                id: "pages.account.settings.sessions.filterStatus",
                defaultMessage: "筛选状态",
              })}
              allowClear
              style={{ width: 120 }}
              value={historyFilters.status}
              onChange={(value) => {
                setHistoryFilters({ ...historyFilters, status: value });
              }}
              options={[
                {
                  label: intl.formatMessage({
                    id: "pages.account.settings.sessions.status.success",
                    defaultMessage: "成功",
                  }),
                  value: "success",
                },
                {
                  label: intl.formatMessage({
                    id: "pages.account.settings.sessions.status.failed",
                    defaultMessage: "失败",
                  }),
                  value: "failed",
                },
              ]}
            />
            <RangePicker
              value={historyFilters.dateRange}
              onChange={(dates) => {
                setHistoryFilters({
                  ...historyFilters,
                  dateRange: dates as [dayjs.Dayjs, dayjs.Dayjs],
                });
              }}
            />
            <Button
              type="primary"
              onClick={() => loadHistory(1, historyPagination.pageSize)}
            >
              {intl.formatMessage({
                id: "pages.account.settings.sessions.search",
                defaultMessage: "查询",
              })}
            </Button>
          </Space>
        </div>
      </div>

      {historyLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : historyRecords.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={intl.formatMessage({
            id: "pages.account.settings.sessions.historyEmpty",
            defaultMessage: "暂无登录记录",
          })}
        />
      ) : (
        <Table
          columns={historyColumns}
          dataSource={historyRecords}
          rowKey="id"
          size="middle"
          pagination={{
            ...historyPagination,
            showSizeChanger: true,
            showTotal: (total) =>
              intl.formatMessage(
                {
                  id: "pages.account.settings.sessions.historyTotal",
                  defaultMessage: "共 {total} 条记录",
                },
                { total }
              ),
            onChange: (page, pageSize) => loadHistory(page, pageSize),
          }}
        />
      )}
    </div>
  );

  return (
    <div className={sStyles.viewWrap}>
      <div className={sStyles.switchBar}>
        <Segmented
          value={activeView}
          onChange={(v) => {
            const next = v as "sessions" | "history";
            setActiveView(next);
            if (next === "history" && historyRecords.length === 0) {
              loadHistory();
            }
          }}
          options={[
            {
              value: "sessions",
              label: (
                <Space size={6}>
                  <DesktopOutlined />
                  {intl.formatMessage({
                    id: "pages.account.settings.sessions.tab.devices",
                    defaultMessage: "登录设备",
                  })}
                </Space>
              ),
            },
            {
              value: "history",
              label: (
                <Space size={6}>
                  <HistoryOutlined />
                  {intl.formatMessage({
                    id: "pages.account.settings.sessions.tab.history",
                    defaultMessage: "登录历史",
                  })}
                </Space>
              ),
            },
          ]}
        />
      </div>
      {activeView === "sessions" ? renderSessionsTab() : renderHistoryTab()}
    </div>
  );
};

export default SessionsView;
